import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Row, Col, message, Collapse, Switch, Tabs, Skeleton, Divider, Typography, Space, Tooltip, Button, Tag, Dropdown, Menu, Modal, Input } from "antd";
import {
  FileText,
  DollarSign,
  Printer,
  ShieldCheck,
  Building2,
  MapPin,
  Phone,
  Smartphone,
  Mail,
  Map,
  Globe,
  Building,
  Landmark,
  Flag,
  Zap,
  Calendar,
  History,
  Send,
  FileSignature,
  Award,
  Maximize,
  Box,
  Layout,
  ClipboardList,
  Users,
  Search,
  Eye,
  Edit3,
  Fingerprint,
  Activity,
  Target,
  Tag as TagIcon,
  Cpu,
  Gauge,
  Wrench,
  ScrollText,
  Power,
  Layers,
  Network,
  MoreVertical,
  RefreshCw,
  Info,
  UtilityPole,
  Shapes,
  Ruler,
  IdCard,
  Store
} from "lucide-react";
import {
  AuditOutlined,
  EnvironmentOutlined,
  PartitionOutlined,
  FileTextOutlined,
  TagOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import VisitPreview from "./VisitPreview";
import MainTitle from "./MainTitle";
import Subtitle from "./Subtitle";
import DataGroup from "./DataGroup";
import DataItem from "./DataItem";
import AnimateItems from "../../utils/AnimateItems";
import SleekTemplateView from "./SleekTemplateView";
import HorizontalTimeline from "./HorizontalTimeline";
import "./VerificationPreview.css";

const { Text, Title } = Typography;

export default function VerificationPreview() {
  const { id } = useParams();
  const [verification, setVerification] = useState();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folioModalVisible, setFolioModalVisible] = useState(false);
  const [newFolio, setNewFolio] = useState("");

  const fetchData = () => {
    setLoading(true);
    const fetchVerification = axios.get(`${process.env.REACT_APP_API_URL}/verificaciones/${id}`);
    const fetchVisits = axios.get(`${process.env.REACT_APP_API_URL}/visitas/${id}`);

    Promise.all([fetchVerification, fetchVisits])
      .then(([verRes, visitRes]) => {
        setVerification(verRes.data[0]);
        setNewFolio(verRes.data[0]?.ControlInterno || "");
        setVisits(visitRes.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message);
      });
  };

  const handleUpdateFolio = () => {
    if (!newFolio.trim()) {
      return message.warning("El folio no puede estar vacío");
    }

    const updatedData = {
      ...verification,
      ControlInterno: newFolio
    };

    axios.put(`${process.env.REACT_APP_API_URL}/verificaciones/${id}`, updatedData)
      .then(() => {
        message.success("Folio actualizado correctamente");
        setFolioModalVisible(false);
        fetchData();
      })
      .catch((err) => {
        message.error("Error al actualizar el folio: " + err.message);
      });
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const templateFields = {
    solicitud_contrato: {
      "Razón social": verification?.NombreCompletoPV,
      "Dato de identificación de la Razón social": verification?.DatoPV_T,
      "Clasificación del SCIAN": verification?.ClasificacionSCIAN,
      "Nombre de la calle": verification?.NombreCalle,
      "Número oficial": verification?.NumeroOficial,
      Colonia: verification?.ColoniaOAsentamiento,
      "Código postal": verification?.CodigoPostal,
      Municipio: verification?.Municipio,
      Ciudad: verification?.Ciudad,
      Estado: verification?.Estado,
      "Teléfono móvil Razón social": verification?.TelefonoMovilPV_T,
      "Correo electrónico de la Razón social": verification?.CorreoElectronicoPV_T,
      "Nombre de la persona que firma el contrato": verification?.NombreCompletoPF,
      "Dato de identificación de la persona que firma el contrato": verification?.DatoPF_T,
      "Teléfono móvil de la persona que firma el contrato": verification?.TelefonoMovilPF_T,
      "Correo electrónico de la persona que firma el contrato": verification?.CorreoElectronicoPF_T,
      "Nombre de la persona que atiende la visita": verification?.NombreCompletoPA,
      "Dato de identificación de la persona que atiende la visita": verification?.DatoPA_T,
      "Teléfono móvil de la persona que atiende la visita": verification?.TelefonoMovilPA_T,
      "Correo electrónico de la persona que atiende la visita": verification?.CorreoElectronicoPA_T,
      "Tensión eléctrica de suministro": verification?.TensionElectricaSuministro,
      "Capacidad de la subestación": verification?.Capacidad,
      "Tipo de instalación": verification?.TipoInstalacion,
      "Tipo de verificación": verification?.TipoVerificacion,
      "Carga instalada": verification?.CargaInstalada,
      "Alcance de la verificación": verification?.AlcanceVerificacion,
      "Fecha de recepción de la solicitud": verification?.FechaRecepcion,
      "Nombre de la persona que solicita la verificación": verification?.NombreCompletoPS,
      "Tipo de identificación de la persona que firma el contrato": verification?.TipoDatoPF_T,
      Monto: verification?.Monto,
      "Fecha del contrato": verification?.FechaContrato,
      "Cargo de la persona que atiende la visita": verification?.CargoPA,
      "Documento de origen del folio de identificación de la persona que atiende la visita": verification?.DocumentoOrigenPA,
      "Folio de la identificación de la persona que atiende la visita": verification?.FolioPA_T,
      "Entidad que expide la identificación de la persona que atiende la visita": verification?.ExpedidoPorPA,
      "Domicilio de la persona que atiende la visita": verification?.DomicilioPA_T,
    },
    // ... other template types kept as they are logic-bound
  };

  const responsiveInfo = {
    xs: 24,
    sm: 24,
    md: 24,
    lg: 12,
    xl: 12,
    xxl: 8,
  };

  return (
    <div className="verification-preview-container">
      {/* Professional Header Card */}
      <div className="verification-header-card">
        <Row gutter={[24, 16]} align="middle">
          {/* Main Info */}
          <Col xs={24} sm={24} md={12} lg={14}>
            <div className="header-main-info">
              <div className="folio-container">
                <Text style={{ 
                  color: "rgba(0,0,0,0.45)", 
                  fontWeight: 600, 
                  fontSize: "11px", 
                  letterSpacing: "0.5px",
                  textTransform: "uppercase" 
                }}>
                  Folio:
                </Text>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Text style={{ fontSize: "16px", fontWeight: 700, color: "#262626" }}>
                    {verification?.ControlInterno || "---"}
                  </Text>
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item key="modify" icon={<Edit3 size={14} />} onClick={() => {
                          setNewFolio(verification?.ControlInterno || "");
                          setFolioModalVisible(true);
                        }}>
                          Modificar folio
                        </Menu.Item>
                        <Menu.Item key="refresh" icon={<RefreshCw size={14} />} onClick={fetchData}>
                          Recargar
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={['click']}
                  >
                    <Button 
                      type="text" 
                      size="small" 
                      icon={<MoreVertical size={16} color="rgba(0,0,0,0.45)" />} 
                      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                    />
                  </Dropdown>
                </div>
              </div>
              
              <div style={{ marginTop: 2 }}>
                <Text style={{ 
                  fontSize: 14, 
                  fontWeight: 600, 
                  color: verification?.NombreCompletoPV ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.25)" 
                }} ellipsis>
                  {verification?.NombreCompletoPV || "Sin razón social"}
                </Text>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "inherit" }}>
                <MapPin size={12} color="rgba(0,0,0,0.45)" />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {verification?.Ciudad || verification?.Estado 
                    ? `${verification?.Ciudad || ""}${verification?.Ciudad && verification?.Estado ? ", " : ""}${verification?.Estado || ""}`
                    : "Ubicación no especificada"}
                </Text>
              </div>
            </div>
          </Col>

          {/* Quick Stats & Status */}
          <Col xs={24} sm={24} md={12} lg={10}>
            <div style={{ 
              display: "flex", 
              justifyContent: "flex-end", 
              gap: "40px", 
              alignItems: "flex-start",
              marginTop: "8px",
              // On mobile (xs/sm/md below 768px in AntD or 991 for md in our CSS), 
              // align everything properly if it stacks
              flexWrap: "wrap",
              justifyContent: "space-between" // Use space-between for small screens if they stay side-by-side
            }} className="header-stats-container">
              <div className="header-info-item" style={{ textAlign: "left", minWidth: "fit-content" }}>
                <span className="header-info-label">Estatus</span>
                <span style={{ 
                  fontSize: "13px", 
                  fontWeight: 700, 
                  color: verification?.FechaEmision || verification?.FolioEmision ? "#52c41a" : "#faad14",
                  display: "block"
                }}>
                  {verification?.FechaEmision || verification?.FolioEmision ? "Emitida" : "Sin emitir"}
                  {verification?.FechaEmision && (
                    <span style={{ fontWeight: 500, marginLeft: "4px", fontSize: "12px", color: "rgba(0,0,0,0.45)" }}>
                      ({moment(verification.FechaEmision).format("DD/MM/YYYY")})
                    </span>
                  )}
                </span>
                <span style={{ fontSize: "11px", color: "rgba(0,0,0,0.45)", marginTop: "-2px" }}>
                  {verification?.FolioEmision ? `SEDIVER: ${verification.FolioEmision}` : "Folio pendiente"}
                </span>
              </div>

              <div className="header-info-item" style={{ textAlign: "right", minWidth: "100px" }}>
                <span className="header-info-label">Monto de contrato</span>
                <span className="header-info-value" style={{ color: verification?.Monto ? "inherit" : "rgba(0,0,0,0.25)", display: "block" }}>
                  {verification?.Monto 
                    ? `$${Number(verification.Monto).toLocaleString()}` 
                    : "Sin monto"}
                </span>
                <span style={{ fontSize: "11px", color: "rgba(0,0,0,0.45)", marginTop: "-2px", display: "block" }}>
                  {verification?.AlcanceVerificacion ? `${verification.AlcanceVerificacion} kW` : "Alcance pendiente"}
                </span>
              </div>
            </div>
          </Col>
        </Row>

        <Divider className="hide-on-md-below" style={{ margin: "16px 0", opacity: 0.4 }} />
        
        {/* Timeline Integration */}
        <div className="hide-on-md-below" style={{ marginTop: -8, marginBottom: -16 }}>
          <HorizontalTimeline verification={verification} visits={visits} />
        </div>
      </div>

      <Tabs 
        type="line" 
        className="custom-tabs"
        onChange={(key) => { if (key === "4") fetchData(); }}
      >
        <Tabs.TabPane
          tab={
            <>
              <ClipboardList size={16} />
              <span>Verificación</span>
            </>
          }
          key="1"
        >
          <div style={{ padding: "8px 4px" }}>
          <AnimateItems delay={0.15}>
            <Subtitle title="Datos generales" marginTop={0} />
            <Row gutter={[16, 16]}>
              <Col {...responsiveInfo}>
                <DataGroup 
                  title={"General"} 
                  formName={"Información general"} 
                  mainData={verification} 
                  setMainData={setVerification}
                  icon={<FileText size={18} />}
                >
                  <DataItem keyName="Descripción breve" value={verification?.DescripcionBreve} fieldWidth={10} loading={loading} />
                  <DataItem icon={<DollarSign size={15} />} iconColor="#82cc4b" keyName="Monto de contrato" value={verification?.Monto} valueType="double" fieldWidth={10} prefix="$" loading={loading} />
                  <DataItem icon={<Printer size={15} />} iconColor="#1890ff" keyName="Folio AMUVIE" value={verification?.FolioAmuvie} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<ShieldCheck size={15} />} iconColor="#fa8c16" keyName="Folio SEDIVER" value={verification?.FolioEmision} valueType="string" fieldWidth={10} loading={loading} />
                </DataGroup>
              </Col>
              <Col {...responsiveInfo}>
                <DataGroup title={"Fechas"} formName={"Fechas"} mainData={verification} setMainData={setVerification} icon={<Calendar size={18} />}>
                  <DataItem icon={<History size={15} />} iconColor="#1890ff" keyName="Fecha de creación" value={verification?.FechaCreacion} valueType="date" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Send size={15} />} iconColor="#82cc4b" keyName="Fecha de la solicitud" value={verification?.FechaRecepcion} valueType="date" fieldWidth={10} loading={loading} />
                  <DataItem icon={<FileSignature size={15} />} iconColor="#fa8c16" keyName="Fecha del contrato" value={verification?.FechaContrato} valueType="date" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Award size={15} />} iconColor="#52c41a" keyName="Fecha de emisión" value={verification?.FechaEmision} valueType="date" fieldWidth={10} loading={loading} />
                </DataGroup>
              </Col>
              <Col {...responsiveInfo}>
                <DataGroup title={"Notas internas"} formName={"Notas"} mainData={verification} setMainData={setVerification} icon={<Edit3 size={18} />}>
                  <DataItem icon={<FileText size={15} />} iconColor="#1890ff" keyName="Notas" value={verification?.Notas} valueType="longString" fieldWidth={6} loading={loading} />
                </DataGroup>
              </Col>
            </Row>

            <Subtitle title="Datos de la Razón social" marginTop={16} />
            <Row gutter={[16, 16]}>
              <Col {...responsiveInfo}>
                <DataGroup
                  title={!verification?.NombreCompletoPV ? "Razón social" : verification?.NombreCompletoPV}
                  subtitle={verification?.TipoPersonaPV_T && verification?.NacionalidadPV_T ? "Persona " + verification?.TipoPersonaPV_T + ", " + verification?.NacionalidadPV_T : null}
                  formName={"Información de la Razón social"}
                  mainData={verification}
                  setMainData={setVerification}
                  icon={<Building2 size={18} />}
                >
                  <DataItem icon={<Store size={15} />} iconColor="purple" keyName="Nombre comercial" value={verification?.NombreComercial} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<PartitionOutlined style={{fontSize: "14px"}} />} iconColor="orange" keyName="SCIAN" value={verification?.ClasificacionSCIAN} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<IdCard size={15} />} iconColor="brown" keyName={verification?.TipoDatoPV_T || "Identificación"} value={verification?.DatoPV_T} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Phone size={15} />} iconColor="#1890ff" keyName="Teléfono fijo" value={verification?.TelefonoFijoPV_T} valueType="phone" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Smartphone size={15} />} iconColor="indigo" keyName="Teléfono móvil" value={verification?.TelefonoMovilPV_T} valueType="phone" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Mail size={15} />} iconColor="#1890ff" keyName="Correo" value={verification?.CorreoElectronicoPV_T} valueType="email" fieldWidth={10} loading={loading} />
                </DataGroup>
              </Col>
              <Col {...responsiveInfo}>
                <DataGroup
                  title={!verification?.NombreCalle ? "Domicilio" : `${verification?.NombreCalle}${verification?.NumeroOficial ? ` ${verification.NumeroOficial}` : " S/N"}${verification?.ColoniaOAsentamiento ? `, Col. ${verification.ColoniaOAsentamiento}` : ""}`}
                  subtitle={verification?.EntreCalle1 && verification?.EntreCalle2 ? `Entre ${verification?.EntreCalle1} y ${verification?.EntreCalle2}` : null}
                  formName={"Domicilio de la Razón social"}
                  mainData={verification}
                  setMainData={setVerification}
                  icon={<MapPin size={18} />}
                >
                  <DataItem icon={<Map size={15} />} iconColor="red" keyName="Código postal" value={verification?.CodigoPostal} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Building size={15} />} iconColor="#1890ff" keyName="Ciudad" value={verification?.Ciudad} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Landmark size={15} />} iconColor="#fa8c16" keyName="Municipio" value={verification?.Municipio} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Flag size={15} />} iconColor="#52c41a" keyName="Estado" value={verification?.Estado} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Globe size={15} />} iconColor="red" keyName="Google Maps" value={verification?.LinkMaps} valueType="string" fieldWidth={10} loading={loading} button={verification?.LinkMaps ? "Ver mapa" : null} buttonFunction={() => window.open(verification?.LinkMaps, '_blank')} />
                </DataGroup>
              </Col>
            </Row>

            <Subtitle title="Personas involucradas" marginTop={16} />
            <Row gutter={[16, 16]}>
              <Col {...responsiveInfo}>
                <DataGroup
                  title={!verification?.NombreCompletoPF ? "Firma del contrato" : `Firma: ${verification?.NombreCompletoPF}`}
                  subtitle={verification?.TipoPersonaPF_T && verification?.NacionalidadPF_T ? "Persona " + verification?.TipoPersonaPF_T + ", " + verification?.NacionalidadPF_T : null}
                  formName={"Persona que firma el contrato"}
                  mainData={verification}
                  setMainData={setVerification}
                  icon={<Users size={18} />}
                >
                  <DataItem icon={<IdCard size={15} />} iconColor="brown" keyName={verification?.TipoDatoPF_T || "Identificación"} value={verification?.DatoPF_T} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Smartphone size={15} />} iconColor="indigo" keyName="Móvil" value={verification?.TelefonoMovilPF_T} valueType="phone" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Mail size={15} />} iconColor="#1890ff" keyName="Correo" value={verification?.CorreoElectronicoPF_T} valueType="email" fieldWidth={10} loading={loading} />
                </DataGroup>
              </Col>
              <Col {...responsiveInfo}>
                <DataGroup
                  title={!verification?.NombreCompletoPA ? "Atiende la visita" : `Atiende: ${verification?.NombreCompletoPA}`}
                  subtitle={
                    [
                      verification?.TipoPersonaPA_T && verification?.NacionalidadPA_T ? `Persona ${verification?.TipoPersonaPA_T}, ${verification?.NacionalidadPA_T}` : null,
                      verification?.CargoPA ? `Cargo: ${verification?.CargoPA}` : null
                    ].filter(Boolean).join(" | ") || null
                  }
                  formName={"Persona que atiende la visita"}
                  mainData={verification}
                  setMainData={setVerification}
                  icon={<Users size={18} />}
                >
                  <DataItem icon={<IdCard size={15} />} iconColor="brown" keyName={verification?.TipoDatoFolioPA_T || "Identificación"} value={verification?.FolioPA_T} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Smartphone size={15} />} iconColor="indigo" keyName="Móvil" value={verification?.TelefonoMovilPA_T} valueType="phone" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Mail size={15} />} iconColor="#1890ff" keyName="Correo" value={verification?.CorreoElectronicoPA_T} valueType="email" fieldWidth={10} loading={loading} />
                </DataGroup>
              </Col>
              <Col {...responsiveInfo}>
                <DataGroup
                  title={!verification?.NombreCompletoPS ? "Solicita" : `Solicita: ${verification?.NombreCompletoPS}`}
                  subtitle={verification?.TipoPersonaPS_T && verification?.NacionalidadPS_T ? "Persona " + verification?.TipoPersonaPS_T + ", " + verification?.NacionalidadPS_T : null}
                  formName={"Persona que solicita la verificación"}
                  mainData={verification}
                  setMainData={setVerification}
                  icon={<Users size={18} />}
                >
                  <DataItem icon={<IdCard size={15} />} iconColor="brown" keyName={verification?.TipoDatoPS_T || "Identificación"} value={verification?.DatoPS_T} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Smartphone size={15} />} iconColor="indigo" keyName="Móvil" value={verification?.TelefonoMovilPS_T} valueType="phone" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Mail size={15} />} iconColor="#1890ff" keyName="Correo" value={verification?.CorreoElectronicoPS_T} valueType="email" fieldWidth={10} loading={loading} />
                </DataGroup>
              </Col>
            </Row>

            <Subtitle title="Características de la instalación" marginTop={16} />
            <Row gutter={[16, 16]}>
              <Col {...responsiveInfo}>
                <DataGroup title={"Clasificación"} formName="Clasificación de la verificación" mainData={verification} setMainData={setVerification} icon={<Layers size={18} />}>
                  <DataItem icon={<Info size={15} />} iconColor="#722ed1" keyName="Objeto de la solicitud" value={verification?.ObjetoSolicitud} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<UtilityPole size={15} />} iconColor="#faad14" keyName="Tensión de la acometida" value={verification?.TipoTension} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Shapes size={15} />} iconColor="#1890ff" keyName="Tipo de verificación" value={verification?.TipoVerificacion} valueType="string" fieldWidth={10} loading={loading} />
                  <DataItem icon={<Network size={15} />} iconColor="#52c41a" keyName="Tipo de instalación" value={verification?.TipoInstalacion} valueType="string" fieldWidth={10} loading={loading} />
                </DataGroup>
              </Col>
              <Col {...responsiveInfo}>
                <DataGroup title={"Valores"} formName="Valores de la instalación eléctrica" mainData={verification} setMainData={setVerification} icon={<Zap size={18} />}>
                  <DataItem icon={<Zap size={15} />} iconColor="#faad14" keyName="Tensión de suministro" value={verification?.TensionElectricaSuministro} valueType="double" suffix="V" fieldWidth={12} loading={loading} />
                  <DataItem icon={<Activity size={15} />} iconColor="#13c2c2" keyName="Carga instalada" value={verification?.CargaInstalada} valueType="double" suffix="kW" fieldWidth={12} loading={loading} />
                  <DataItem icon={<Ruler size={15} />} iconColor="#eb2f96" keyName="Alcance de la verificación" value={verification?.AlcanceVerificacion} valueType="double" suffix="kW" fieldWidth={12} loading={loading} />
                  <DataItem icon={<Gauge size={15} />} iconColor="#52c41a" keyName="Capacidad de la subestación" value={verification?.Capacidad} valueType="double" suffix="kVA" fieldWidth={12} loading={loading} />
                </DataGroup>
              </Col>
            </Row>
                      </AnimateItems>
                    </div> {/* Closing div for padding */}
                  </Tabs.TabPane>
          
                  <Tabs.TabPane
                    tab={
                      <>
                        <MapPin size={16} />
                        <span>Visita</span>
                      </>
                    }
                    key="2"
                  >
                    <div style={{ padding: "12px 4px" }}>
                      <VisitPreview id={id} verification={verification} visits={visits} setVisits={setVisits} />
                    </div>
                  </Tabs.TabPane>
          
                  <Tabs.TabPane
                    tab={
                      <>
                        <PartitionOutlined style={{ fontSize: "16px" }} />
                        <span>Formatos</span>
                      </>
                    }
                    key="4"
                  >
                    <div style={{ padding: "12px 4px" }}>
                      <SleekTemplateView
                        id={id}
                        verification={verification}
                        visits={visits}
                        templateFields={templateFields}
                        loading={loading}
                        refresh={fetchData}
                      />
                    </div>
                  </Tabs.TabPane>
                </Tabs>

                <Modal
                  title="Modificar Folio"
                  visible={folioModalVisible}
                  onOk={handleUpdateFolio}
                  onCancel={() => setFolioModalVisible(false)}
                  okText="Actualizar"
                  cancelText="Cancelar"
                >
                  <div style={{ padding: "10px 0" }}>
                    <Text type="secondary" style={{ display: "block", marginBottom: "8px" }}>
                      Ingrese el nuevo número de folio para esta verificación:
                    </Text>
                    <Input
                      placeholder="Ej: UVIE/2026/001"
                      value={newFolio}
                      onChange={(e) => setNewFolio(e.target.value)}
                      onPressEnter={handleUpdateFolio}
                    />
                  </div>
                </Modal>
              </div>
            );
          }
